import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wand2, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import EditableVisualBlocks, { countFlags } from './EditableVisualBlocks';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

// Suggests visuals for every section at once; admin reviews and accepts the ticked sections.
export default function EnhanceAllVisualsButton({ courseId, token, onSaved }) {
  const [proposals, setProposals] = useState(null);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const propose = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/ai/course/${courseId}/enhance-all-visuals`, {}, { headers, timeout: 180000 });
      const list = res.data.sections || [];
      setSelected(Object.fromEntries(list.filter((s) => s.blocks.length).map((s) => [s.index, true])));
      setProposals(list);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'NAVI could not create visuals');
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    const items = proposals.filter((s) => selected[s.index] && s.blocks.length).map((s) => ({ index: s.index, blocks: s.blocks }));
    if (!items.length) return toast.error('Tick at least one section');
    setBusy(true);
    try {
      const res = await axios.put(`${API}/ai/course/${courseId}/visual-blocks/bulk`, { items }, { headers });
      onSaved(res.data.sections || {});
      toast.success(`Visuals saved to ${res.data.updated} section${res.data.updated === 1 ? '' : 's'}. Mark each reviewed to show learners`);
      setProposals(null);
    } catch (e) {
      toast.error('Could not save visuals');
    } finally {
      setBusy(false);
    }
  };

  const count = proposals ? proposals.filter((s) => selected[s.index]).length : 0;
  return (
    <>
      <button onClick={propose} disabled={busy} className="w-full flex items-center justify-center gap-2 p-3 rounded-lg bg-violet-600 hover:bg-violet-500 text-white font-medium transition-colors disabled:opacity-60" data-testid="enhance-all-visuals-btn">
        {busy && !proposals ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
        {busy && !proposals ? 'NAVI is designing visuals…' : 'Enhance all sections'}
      </button>
      <Dialog open={!!proposals} onOpenChange={(o) => !o && setProposals(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="enhance-all-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Review NAVI's visuals for every section</DialogTitle>
            <DialogDescription className="text-zinc-400">NAVI only uses facts from each section and flags anything it can't find there. Edit inline, then accept the ticked sections. Existing visuals are replaced. Saved visuals stay hidden from learners until you mark them reviewed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {(proposals || []).map((s) => (
              <section key={s.index} className="rounded-xl border border-zinc-800 p-4" data-testid={`enhance-all-section-${s.index}`}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox checked={!!selected[s.index]} disabled={!s.blocks.length} onCheckedChange={(v) => setSelected({ ...selected, [s.index]: !!v })} data-testid={`enhance-all-check-${s.index}`} />
                  <span className="text-white font-semibold flex-1">{s.index + 1}. {s.title}</span>
                  {s.had_existing && s.blocks.length > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300">will replace existing</span>}
                  {countFlags(s.blocks) > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300">{countFlags(s.blocks)} to verify</span>}
                  {!s.blocks.length && <span className="text-[11px] text-zinc-500">No suitable visuals</span>}
                </label>
                {s.blocks.length > 0 && <EditableVisualBlocks blocks={s.blocks} testIdPrefix={`enhance-all-preview-${s.index}`}
                  onChange={(blocks) => setProposals((list) => list.map((x) => (x.index === s.index ? { ...x, blocks } : x)))} />}
              </section>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProposals(null)} className="border-zinc-700 text-zinc-300" data-testid="enhance-all-discard-btn">Discard</Button>
            <Button onClick={accept} disabled={busy || !count} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="enhance-all-accept-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}Accept selected ({count})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
